import { FC } from 'react'

import { useParams } from 'react-router-dom'

import {
  CreateForm,
  type CreateFormProps,
} from 'core/components/lib/DynamicForm'
import { Page } from 'core/components/lib/Page'
import PageHeader from 'core/components/lib/PageHeader'
import { ResourceCatalogEntry } from 'core/types/generated/resources'
import { StaticFieldSchema } from 'core/utils/generated/formSchemas'

import { JsonRecord } from './deriveColumns'
import ResourceBackButton from './ResourceBackButton'
import {
  resourceBackFallbackPath,
  resourceDetailPath,
  resourceListPath,
} from './resourcePaths'
import useResourceNavigate from './useResourceNavigate'

type PassthroughProps = Pick<
  CreateFormProps<JsonRecord>,
  | 'arraySchemas'
  | 'fieldOverrides'
  | 'selectOptions'
  | 'transformSubmit'
  | 'validationRules'
>

type ResourceCreateViewProps = PassthroughProps & {
  resource: ResourceCatalogEntry
  /** A generated *request* schema. Fields render from this, not from data. */
  schema: Array<StaticFieldSchema>
  /**
   * Sends the new record and answers with its id, which the view needs to
   * land on the record it just made. No id means it stays put.
   */
  onCreate: (body: JsonRecord) => Promise<string | undefined>
}

const ResourceCreateView: FC<ResourceCreateViewProps> = ({
  resource,
  schema,
  onCreate,
  ...formProps
}) => {
  const params = useParams<{ [param: string]: string }>()
  const navigate = useResourceNavigate()

  const label = `Create ${resource.label.singular}`

  return (
    <Page variant='full'>
      <PageHeader
        back={
          <ResourceBackButton
            fallbackPath={resourceListPath(resource.key, {
              companyId: params.companyId,
            })}
          />
        }
        title={label}
      />
      <CreateForm
        {...formProps}
        // Cancel lands on the list wherever the user came from, so an
        // abandoned create never drops them back into another create form.
        onCancel={() =>
          navigate(
            resourceBackFallbackPath(resource.key, {
              companyId: params.companyId,
              hasList: resource.operations.list != null,
            }),
          )
        }
        onSubmit={async (payload) => {
          const id = await onCreate(payload as JsonRecord)
          if (id != null) {
            navigate(
              resourceDetailPath(resource.key, {
                companyId: params.companyId,
                id,
              }),
            )
          }
        }}
        schema={schema}
        submitText={label}
      />
    </Page>
  )
}

export default ResourceCreateView
